import { useMemo, useState } from 'react';
import {
  AllocationHook,
  PopupHook,
  useUnassignedAllocationRefresh,
} from '../../../../../hooks';
import {
  AssignAllocationPopup,
  UnassignedAllocationsAntTable,
} from '../../../../../page-components';
import { useWavesFilterContext } from '.';
import { ToastMessage, AllocationService, OrderService } from '../../../../../utility';
import { ConfirmationPopup } from '../../../../../components';
import { ArrowsClockwise, ArrowsLeftRight, Waves } from '@phosphor-icons/react';
import { ChangeLocationPopup } from '@page-components/waves/popup/ChangeLocationPopup';
import { CreateAllocationWavePopup } from '@page-components/waves/popup/CreateWavePopup';
import { Button, Flex, Input, Table, Tooltip } from 'antd';

function buildUnassignedGroups(data = []) {
  const groupsByKey = new Map();

  data.forEach(allocation => {
    const product = allocation.inventory?.product || {};
    const customer = allocation.customerOrganization || {};
    const productId = product.id || product._id || allocation.productId || 'unknown';
    const customerId = customer.id || customer._id || 'none';
    const key = `${productId}|${customerId}`;

    if (!groupsByKey.has(key)) {
      groupsByKey.set(key, {
        key,
        productId,
        customerId,
        sku: product.sku || product.p_sku || 'N/A',
        name: product.name || product.p_title || 'Unknown Item',
        customerName: customer.name || 'N/A',
        totalQuantity: 0,
        orders: new Set(),
        allocations: [],
      });
    }

    const group = groupsByKey.get(key);
    group.totalQuantity += Number(allocation.quantity) || 0;
    if (allocation.order?.marketplaceOrderDisplayId) {
      group.orders.add(allocation.order.marketplaceOrderDisplayId);
    }
    group.allocations.push(allocation);
  });

  return Array.from(groupsByKey.values()).map(group => ({
    ...group,
    orderCount: group.orders.size,
    orderIds: Array.from(group.orders),
    allocationIds: group.allocations.map(allocation => allocation.id),
  }));
}

function UnassignedSkuGroupsTable({ groups, allocationSelection, toggleGroupSelection }) {
  if (!groups.length) return null;

  const selectedRowKeys = groups
    .filter(group => group.allocationIds.every(id => allocationSelection[id]?.selected))
    .map(group => group.key);

  return (
    <Table
      className="mb-3"
      size="small"
      rowKey="key"
      dataSource={groups}
      pagination={false}
      bordered
      rowSelection={{
        selectedRowKeys,
        preserveSelectedRowKeys: true,
        onSelect: (record, selected) => toggleGroupSelection(record, selected),
      }}
      expandable={{
        expandedRowRender: record => (
          <div>
            {record.allocations.map(allocation => (
              <div key={allocation.id} className="d-flex justify-content-between">
                <span>{allocation.order?.marketplaceOrderDisplayId}</span>
                <span>{allocation.inventory?.location?.code}</span>
                <span>{allocation.quantity} units</span>
              </div>
            ))}
          </div>
        ),
      }}
      columns={[
        {
          title: 'SKU',
          dataIndex: 'sku',
          width: '20%',
        },
        {
          title: 'Item',
          dataIndex: 'name',
          width: '32%',
        },
        {
          title: 'Total Required',
          dataIndex: 'totalQuantity',
          width: '14%',
        },
        {
          title: 'Orders',
          dataIndex: 'orderCount',
          width: '12%',
        },
        {
          title: 'Customer',
          dataIndex: 'customerName',
          width: '22%',
        },
      ]}
    />
  );
}

function AutoAssignUnassignedButton({ onRefresh, disabled, isProcessing }) {
  return (
    <Button loading={isProcessing} onClick={onRefresh} disabled={disabled}>
      <ArrowsClockwise className="mr-1" size={16} />
      {isProcessing ? 'Running...' : 'Run Auto Assign'}
    </Button>
  );
}

function Header({
  allocationSelection,
  setAllocationSelection,
  openChangeLocationPopup,
  openCreateAllocationWavePopup,
  createBatchTransfer,
  onRefresh,
  refreshDisabled,
  isProcessing,
  batchTransferLoading,
  search,
  setSearch,
}) {
  const selectedAllocations = useMemo(() => {
    return Object.values(allocationSelection).filter(x => x.selected);
  }, [allocationSelection]);

  const sameProductIdSelected = useMemo(() => {
    return selectedAllocations.every(
      x =>
        x.productId === selectedAllocations[0].productId &&
        x.customerId === selectedAllocations[0].customerId,
    );
  }, [selectedAllocations]);

  const allAllocationAreSameConfig = useMemo(() => {
    return selectedAllocations.every(
      x => x.requiresTote === selectedAllocations[0].requiresTote,
    );
  }, [selectedAllocations]);

  return (
    <Flex justify="end" align="center" gap={8} className="mb-2">
      <AutoAssignUnassignedButton
        onRefresh={onRefresh}
        disabled={refreshDisabled}
        isProcessing={isProcessing}
      />
      {selectedAllocations.length > 0 &&
        (sameProductIdSelected ? (
          <Button onClick={openChangeLocationPopup}>
            <ArrowsLeftRight className="mr-1" size={16} />
            Change Location ({selectedAllocations.length})
          </Button>
        ) : (
          <Tooltip title="Select same product and customer allocations to change location.">
            <Button onClick={() => setAllocationSelection({})} disabled>
              <ArrowsLeftRight className="mr-1" size={16} />
              Change Location
            </Button>
          </Tooltip>
        ))}
      {selectedAllocations.length > 0 &&
        (allAllocationAreSameConfig ? (
          <Button onClick={openCreateAllocationWavePopup}>
            <Waves className="mr-1" size={16} />
            Create Wave
          </Button>
        ) : (
          <Tooltip title="Select same config (tote/pallet) allocations to create wave.">
            <span>
              <Button disabled>
                <Waves className="mr-1" size={16} />
                Create Wave
              </Button>
            </span>
          </Tooltip>
        ))}
      {selectedAllocations.length > 0 && (
        <Button loading={batchTransferLoading} onClick={createBatchTransfer}>
          Create Batch Transfer ({selectedAllocations.length})
        </Button>
      )}
      <Input.Search
        allowClear
        placeholder="Search by Order, Item ..."
        value={search || ''}
        onChange={event => setSearch(event.target.value)}
        style={{ maxWidth: 320 }}
      />
    </Flex>
  );
}

export function UnAssignedListTab() {
  const { warehouseId } = useWavesFilterContext();

  const {
    data,
    totalData,
    loading,
    filter,
    filterChanged,
    onSortChange,
    pageChanged,
    refresh,
  } = AllocationHook('unassigned', warehouseId);

  const { startRefresh, isProcessing } = useUnassignedAllocationRefresh(
    warehouseId,
    refresh,
  );

  const {
    open: changeLocationPopup,
    handleOpen: openChangeLocationPopup,
    handleClose: closeChangeLocationPopup,
  } = PopupHook();

  const {
    open: createAllocationWavePopup,
    handleOpen: openCreateAllocationWavePopup,
    handleClose: closeCreateAllocationWavePopup,
  } = PopupHook();

  const [allocation, setAllocation] = useState(false);
  const [cancelAllocationPopup, setCancelAllocationPopup] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [batchTransferLoading, setBatchTransferLoading] = useState(false);
  const [allocationSelection, setAllocationSelection] = useState({});

  const unassignedGroups = useMemo(() => buildUnassignedGroups(data || []), [data]);

  const toggleAllocation = (allocation, selected) => {
    if (!allocation?.id) return;

    setAllocationSelection(allocationSelection => ({
      ...allocationSelection,
      [allocation.id]: {
        selected: selected ?? !allocationSelection[allocation.id]?.selected,
        productId: allocation.inventory?.product?.id,
        image: allocation.inventory?.product?.images?.display_image || '',
        name: allocation.inventory?.product?.name,
        sku: allocation.inventory?.product?.sku,
        location: allocation.inventory?.location?.code,
        zone: allocation.inventory?.zone?.code,
        rack: allocation.inventory?.rack?.code,
        color: allocation.inventory?.zone?.color,
        orderId: allocation.order?.marketplaceOrderDisplayId,
        requiresTote: allocation.requiresTote,
        id: allocation.id,
        customerName: allocation?.customerOrganization?.name,
        customerImage: allocation?.customerOrganization?.logo?.url,
        customerId: allocation?.customerOrganization?.id,
      },
    }));
  };

  const toggleGroupSelection = (group, selected) => {
    setAllocationSelection(allocationSelection => {
      const nextSelection = { ...allocationSelection };
      group.allocations.forEach(allocation => {
        nextSelection[allocation.id] = {
          selected,
          productId: allocation.inventory?.product?.id,
          image: allocation.inventory?.product?.images?.display_image || '',
          name: allocation.inventory?.product?.name,
          sku: allocation.inventory?.product?.sku,
          location: allocation.inventory?.location?.code,
          zone: allocation.inventory?.zone?.code,
          rack: allocation.inventory?.rack?.code,
          color: allocation.inventory?.zone?.color,
          orderId: allocation.order?.marketplaceOrderDisplayId,
          requiresTote: allocation.requiresTote,
          id: allocation.id,
          customerName: allocation?.customerOrganization?.name,
          customerImage: allocation?.customerOrganization?.logo?.url,
          customerId: allocation?.customerOrganization?.id,
        };
      });
      return nextSelection;
    });
  };

  const assignAllocation = allocation => {
    setAllocation(allocation);
  };

  const onClose = () => {
    setAllocation(null);
  };

  const onAssignItem = async waveId => {
    try {
      setProcessing(true);
      await AllocationService.assignAllocationToWave(waveId, allocation.id);
      onClose();
      ToastMessage.success('Item assigned to wave successfully');
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  const fulfillOrder = async item => {
    try {
      setProcessing(true);
      const payload = {
        allocations: [
          {
            quantity: item.quantity,
            allocationId: item.id,
          },
        ],
      };
      await OrderService.fulfillTote(item.order?.id, payload);
      ToastMessage.success('Order marked as fulfilled Successfully');
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  const onCloseConfirmation = () => {
    setCancelAllocationPopup(null);
  };

  const onConfirm = async () => {
    try {
      setProcessing(true);
      await AllocationService.cancelAllocation(cancelAllocationPopup.id);
      ToastMessage.success('Allocation cancelled successfully');
      onCloseConfirmation();
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  const onClickCancel = item => {
    setCancelAllocationPopup(item);
  };

  const createBatchTransfer = async () => {
    const selectedAllocationIds = Object.values(allocationSelection)
      .filter(x => x.selected)
      .map(x => x.id);

    if (!selectedAllocationIds.length) {
      ToastMessage.error('Please select at least one grouped SKU');
      return;
    }

    try {
      setBatchTransferLoading(true);
      const response = await AllocationService.createUnassignedBatchTransfer({
        warehouseId,
        allocationIds: selectedAllocationIds,
      });
      const waveNumber = response?.wave?.waveNumber;
      ToastMessage.success(
        waveNumber
          ? `Batch transfer ${waveNumber} created successfully`
          : 'Batch transfer request processed',
      );
      setAllocationSelection({});
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setBatchTransferLoading(false);
    }
  };

  return (
    <div className="">
      <Header
        allocationSelection={allocationSelection}
        setAllocationSelection={setAllocationSelection}
        openChangeLocationPopup={openChangeLocationPopup}
        openCreateAllocationWavePopup={openCreateAllocationWavePopup}
        createBatchTransfer={createBatchTransfer}
        onRefresh={startRefresh}
        refreshDisabled={!warehouseId}
        isProcessing={isProcessing}
        batchTransferLoading={batchTransferLoading}
        search={filter.search}
        setSearch={value => filterChanged('search', value)}
      />
      <UnassignedSkuGroupsTable
        groups={unassignedGroups}
        allocationSelection={allocationSelection}
        toggleGroupSelection={toggleGroupSelection}
      />
      <UnassignedAllocationsAntTable
        loading={loading}
        data={data || []}
        totalData={totalData}
        filter={filter}
        filterChanged={filterChanged}
        onSortChange={onSortChange}
        pageChanged={pageChanged}
        fulfillOrder={fulfillOrder}
        assignAllocation={assignAllocation}
        onClickCancel={onClickCancel}
        toggleAllocation={toggleAllocation}
        allocationSelection={allocationSelection}
      />
      <AssignAllocationPopup
        open={allocation}
        onClose={onClose}
        warehouseId={warehouseId}
        allocation={allocation}
        onAssignItem={onAssignItem}
        processing={processing}
      />
      <ConfirmationPopup
        open={!!cancelAllocationPopup}
        title="Cancel Allocation"
        message={`This will remove allocaion in order ${cancelAllocationPopup?.order?.marketplaceOrderDisplayId}`}
        onClose={onCloseConfirmation}
        onConfirm={onConfirm}
        loading={processing}
        closeText="Close"
        confirmText="Remove"
      />
      <ChangeLocationPopup
        allocationSelection={allocationSelection}
        setAllocationSelection={setAllocationSelection}
        open={changeLocationPopup}
        onClose={closeChangeLocationPopup}
        refresh={refresh}
        warehouseId={warehouseId}
      />
      <CreateAllocationWavePopup
        warehouseId={warehouseId}
        open={createAllocationWavePopup}
        onClose={closeCreateAllocationWavePopup}
        refresh={refresh}
        allocationSelection={allocationSelection}
        setAllocationSelection={setAllocationSelection}
      />
    </div>
  );
}
